import type { FC, ReactNode } from 'react'

import { RevealImage } from '@/components/motion/reveal-image'
import { Reveal } from '@/components/motion/reveal'
import { Parallax } from '@/components/motion/parallax'
import { contact, social } from '@/constants/site'

const availability = [
    { period: 'Spring 2026', state: 'Two dates left', open: true },
    { period: 'Summer 2026', state: 'Fully booked', open: false },
    { period: 'Autumn 2026', state: 'Open', open: true },
    { period: 'Winter 2026', state: 'Open', open: true }
]

export const ContactDetails: FC = (): ReactNode => {
    return (
        <section id='details' className='edge bg-background py-20 md:py-28'>
            <div className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
                <div className='lg:col-span-5'>
                    <Parallax speed={44}>
                        <RevealImage
                            src='/images/portrait-3.jpg'
                            alt='A portrait sitting lit by a single window'
                            className='aspect-[4/5] w-full'
                            sizes='(max-width: 1024px) 100vw, 40vw'
                            from='bottom'
                        />
                    </Parallax>
                </div>

                <div className='lg:col-span-7'>
                    <Reveal className='label mb-8 text-muted-foreground'>
                        <span className='text-[color:var(--gold-ink)]'>&#9679;</span> The practical part
                    </Reveal>

                    <div className='grid gap-10 sm:grid-cols-2'>
                        <Reveal stagger={0.08} className='space-y-3'>
                            <p className='label text-muted-foreground'>Email</p>

                            <a
                                href={`mailto:${contact.email}`}
                                className='link-wipe block text-lg text-foreground'
                            >
                                {contact.email}
                            </a>

                            <p className='text-xs text-muted-foreground'>
                                {contact.response}
                            </p>
                        </Reveal>

                        <Reveal stagger={0.08} className='space-y-3' delay={0.05}>
                            <p className='label text-muted-foreground'>Phone</p>

                            <a
                                href={`tel:${contact.phone.replace(/\s/g, '')}`}
                                className='link-wipe block text-lg text-foreground'
                            >
                                {contact.phone}
                            </a>

                            <p className='text-xs text-muted-foreground'>
                                {contact.hours}
                            </p>
                        </Reveal>

                        <Reveal stagger={0.08} className='space-y-3' delay={0.1}>
                            <p className='label text-muted-foreground'>Studio</p>

                            <address className='space-y-1 text-sm not-italic leading-relaxed text-foreground/85'>
                                {contact.address.map((line) => (
                                    <span key={line} className='block'>
                                        {line}
                                    </span>
                                ))}
                            </address>

                            <p className='text-xs text-muted-foreground'>
                                Visits by arrangement, the buzzer is unmarked
                            </p>
                        </Reveal>

                        <Reveal stagger={0.08} className='space-y-3' delay={0.15}>
                            <p className='label text-muted-foreground'>Elsewhere</p>

                            <ul className='space-y-1.5'>
                                {social.map((item) => (
                                    <li key={item.label}>
                                        <a
                                            href={item.href}
                                            target='_blank'
                                            rel='noreferrer noopener'
                                            className='link-wipe text-sm text-foreground/85'
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>

                    <div className='mt-14 border-t border-border pt-8'>
                        <Reveal className='label mb-6 text-muted-foreground'>
                            Availability
                        </Reveal>

                        <ul className='divide-y divide-border'>
                            {availability.map((slot) => (
                                <li key={slot.period}>
                                    <Reveal className='flex items-baseline justify-between gap-6 py-4'>
                                        <span className='text-base text-foreground/85'>
                                            {slot.period}
                                        </span>

                                        <span
                                            className={
                                                slot.open
                                                    ? 'label text-[color:var(--gold-ink)]'
                                                    : 'label text-muted-foreground line-through'
                                            }
                                        >
                                            {slot.state}
                                        </span>
                                    </Reveal>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    )
}
